<template>
  <div ref="containerRef" class="section-smart-folders-container relative h-full">
    <div class="flex flex-col h-full">
      <div ref="toolbarRef">
        <CSectionSmartFoldersToolbar
          v-model:search="searchTerm"
          :added-item-ids="props.addedSectionIds"
          :current-sort="currentSort"
          :mode="props.mode"
          :selected-item-ids="getSelectedSections().map((s) => s.id)"
          @breadcrumb-click="onBreadcrumbClick"
          @breadcrumb-reset="onBreadcrumbReset"
          @clear-selection="emit('clear-selection')"
          @configure="onSmartFoldersConfigure"
          @items-added="(itemIds, position) => emit('sections-bulk-add', getSelectedSections(), position)"
          @items-removed="() => emit('sections-bulk-remove', getSelectedSections())"
          @sort-changed="onSortChanged"
        />
      </div>
      <NGrid class="h-full overflow-hidden" cols="12" item-responsive>
        <NGridItem class="h-full overflow-auto" :span="isContentPanelOpen ? '12 750:7 850:8 1400:9' : '12'">
          <div
            class="section-smart-folders"
            :class="{ 'pr-3': isContentPanelOpen }"
            :style="isSelector ? { height: contentHeight, overflow: 'auto' } : { height: '100%' }"
          >
            <div v-if="store.state.isLoading" class="flex flex-col items-center justify-center h-full">
              <NSpin />
            </div>
            <SmartFoldersMissingConfig v-else-if="!store.state.hierarchyLevels?.length" hide-configuration-button />
            <div v-else>
              <CList
                ref="listRef"
                class="rounded-2 flex bg-base"
                :data="[...displayItems]"
                :height="isSelector ? parseInt(contentHeight) : undefined"
                :max-row-height="store.state.viewMode === 'sections' ? 200 : 50"
                :movable="isSelector && store.state.viewMode === 'sections'"
                :selectedIds="isSelector ? props.selectedSectionIds || [] : []"
                @item:click="onRowClick"
                @item:drop="onDrop"
              >
                <template #item="{ item, isSelected, isDraggedOver, isContextMenuOpen }">
                  <SmartFolderListItem
                    :allow-row-highlight="false"
                    :filters="computed(() => ({}) as Record<string, any>)"
                    :is-context-menu-open="isContextMenuOpen"
                    :is-dragged-over="isDraggedOver"
                    :is-selected="isSelected"
                    :item="item"
                    :name="getSmartFolderDisplayName(item)"
                    :show-item-type="false"
                    :style="{ opacity: item.opacity || 1 }"
                  >
                    <template v-if="store.state.viewMode === 'folders' && item.items_count" #folder-name-suffix>
                      <span class="text-primary ml-1">({{ item.items_count }})</span>
                    </template>
                    <template v-if="isSelector && store.state.viewMode === 'sections'" #actions>
                      <CButton
                        circle
                        class="h-9 w-9"
                        :class="{
                          'bg-base !focus:bg-base !active:bg-base': !isSelected && !isAdded(item as CanvasRetrieve),
                          'bg-primary5': isSelected,
                          'bg-error2 !focus:bg-error2 !active:bg-error2 ': isAdded(item as CanvasRetrieve),
                        }"
                        secondary
                        type="default"
                        @click.stop="handleSectionClick(item as CanvasRetrieve)"
                      >
                        <template #icon>
                          <CIcon v-if="isSelected" class="ml-[2px] color-primary" icon="grip-dots-vertical" size="12" />
                          <CIcon
                            v-else-if="isAdded(item as CanvasRetrieve)"
                            class="ml-[2px] color-error"
                            icon="times"
                            size="12"
                          />
                          <CIcon v-else class="ml-[2px] color-primary" icon="plus" size="12" />
                        </template>
                      </CButton>
                    </template>
                  </SmartFolderListItem>
                </template>
              </CList>
              <NPagination
                v-if="showPagination"
                v-model:page="store.state.page"
                class="mt-4 flex justify-center"
                :page-count="Math.ceil(store.state.totalCount / store.state.pageSize)"
                :page-size="store.state.pageSize"
                @update:page="onPageChange"
              />
            </div>
          </div>
        </NGridItem>
      </NGrid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onBeforeUnmount, watch, nextTick, type ComputedRef } from 'vue'
import { orderBy } from 'lodash-es'
import { NGrid, NGridItem, NPagination, NSpin } from 'naive-ui'
import { useLocalStorage } from '@vueuse/core'
import type { CanvasRetrieve } from '@openapi'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSectionSmartFoldersToolbar from './CSectionSmartFoldersToolbar.vue'
import SmartFoldersMissingConfig from '@lib/components/savedCanvases/smartFolders/SmartFoldersMissingConfig.vue'
import CList from '@lib/components/CList/CList.vue'
import SmartFolderListItem from '@lib/apps/browser/components/Content/Common/SmartFolderListItem.vue'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const emit = defineEmits<{
  'open-section': [section: CanvasRetrieve]
  'edit-section': [section: CanvasRetrieve]
  'section-select': [section: CanvasRetrieve]
  'section-add': [section: CanvasRetrieve]
  'section-remove': [section: CanvasRetrieve]
  'sections-bulk-add': [sections: CanvasRetrieve[], position?: 'top' | 'bottom', targetIndex?: number]
  'sections-bulk-remove': [sections: CanvasRetrieve[]]
  'clear-selection': []
}>()

const props = defineProps<{
  storageKey: string
  mode: 'management' | 'selector'
  selectedSectionIds?: CanvasRetrieve['id'][]
  addedSectionIds?: CanvasRetrieve['id'][]
}>()

const store = useSectionSmartFolderStore()
const listRef = ref()
const showConfigurationModal = ref(false)
const isContentPanelOpen = ref(false)
const searchTerm = ref('')
const DEFAULT_SORTING = { field: 'name', order: 'asc' as const }
const toolbarRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const contentHeight = ref<string>('auto')

const isSelector = computed(() => props.mode === 'selector')

function isFolder(item: any): boolean {
  return item?.type === 'smart_folder'
}

function isAdded(item: CanvasRetrieve): boolean {
  return isSelector.value ? (props.addedSectionIds || []).includes(item.id) : false
}

const displayItems = computed(() => {
  if (!store.state.hierarchyLevels?.length) return []

  let items: any[] = []

  if (store.state.viewMode === 'sections') {
    items =
      store.state.matchingSections?.map((item) => ({
        ...item,
        type: 'section',
      })) ?? []
  } else {
    items =
      store.state.availableValues?.map((item) => ({
        ...item,
        id: item.value,
        type: 'smart_folder',
      })) ?? []
  }

  // Apply search filter if search term exists
  if (searchTerm.value.trim()) {
    const search = searchTerm.value.toLowerCase().trim()
    items = items.filter((item) => {
      // Search by name only
      const name = getSmartFolderDisplayName(item).toLowerCase()
      return name.includes(search)
    })
  }

  const folders = items.filter((item) => isFolder(item))
  const sections = items.filter((item) => !isFolder(item))

  const { field, order } = currentSort.value || DEFAULT_SORTING

  if (!launchDarkly.value.enable_sorting_in_sections) {
    return orderBy(items, ['name'], ['asc'])
  }

  // If default sorting is selected, return items without client-side sorting
  if (field === 'default') {
    return items
  }

  if (field === 'name') {
    const nameSorter = (item: any) => item.name?.toLowerCase?.() ?? (item.value?.toLowerCase?.() || '')
    const sortedFolders = orderBy(folders, [nameSorter], [order])
    const sortedSections = orderBy(sections, [nameSorter], [order])

    return [...sortedFolders, ...sortedSections]
  } else if (field === 'sequence') {
    items = orderBy(
      items,
      [
        (item) => {
          const sequence = item.metadata?.sequence
          return sequence !== undefined && sequence !== null ? Number(sequence) : Number.MAX_SAFE_INTEGER
        },
        (item) => getSmartFolderDisplayName(item).toLowerCase(),
      ],
      [order, 'asc'],
    )
  } else {
    const sortedFolders = orderBy(folders, [field], [order])
    const sortedSections = orderBy(sections, [field], [order])

    return [...sortedFolders, ...sortedSections]
  }

  return items
})

const myUser = inject<ComputedRef<{ id: string }>>(
  'myUser',
  computed(() => ({ id: '' })),
)

const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
  'pitcherInfo',
  computed(() => ({ instanceId: '' })),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const storedPath = useLocalStorage<Array<string>>(
  `section_${isSelector.value ? 'selector_' : ''}smart-folders_${pitcherInfo.value.instanceId}_${myUser.value.id}_current_path`,
  [],
  {
    serializer: {
      read: (v: string) => {
        try {
          return JSON.parse(v)
        } catch {
          return []
        }
      },
      write: (v: Array<string>) => JSON.stringify(v),
    },
  },
)

const showPagination = computed(
  () => store.state.viewMode === 'sections' && store.state.totalCount > store.state.pageSize,
)

function onSmartFoldersConfigure() {
  showConfigurationModal.value = true
}

function onBreadcrumbClick(index: number) {
  store.actions.navigateToLevel(index + 1)

  // Clear search and selections when path changes
  searchTerm.value = ''
  emit('clear-selection')

  const pathValues = store.state.currentPath?.map((p) => p.value) ?? []
  storedPath.value = [...pathValues]
}

function onBreadcrumbReset() {
  store.actions.navigateToLevel(0)

  // Clear search and selections when path changes
  searchTerm.value = ''
  emit('clear-selection')

  storedPath.value = []
}

function onRowClick(item: any) {
  // Handle smart folder items
  if (item.type === 'smart_folder') {
    // Clear search and selections when path changes
    searchTerm.value = ''
    emit('clear-selection')

    store.actions.selectValue(item.value)
    const pathValues = store.state.currentPath?.map((p) => p.value) ?? []
    storedPath.value = [...pathValues]
  }
  // Handle section items
  else if (item.type === 'section') {
    if (isSelector.value) {
      emit('section-select', item as CanvasRetrieve)
    } else {
      emit('open-section', item as CanvasRetrieve)
    }
  }
}

function handleSectionClick(item: CanvasRetrieve) {
  if (!isSelector.value) return

  if (isAdded(item)) {
    emit('section-remove', item)
  } else {
    emit('section-add', item)
  }
}

function onDrop(dropOnItemId?: CanvasRetrieve['id'], index?: number) {
  if (!isSelector.value) return

  // Get selected sections from smart folders
  const selectedSections = getSelectedSections()
  if (selectedSections.length > 0) {
    emit('sections-bulk-add', selectedSections, 'bottom', index)
  }
}

function getSelectedSections(): CanvasRetrieve[] {
  if (!isSelector.value) return []

  return displayItems.value.filter(
    (item) => item.type === 'section' && (props.selectedSectionIds || []).includes(item.id),
  ) as CanvasRetrieve[]
}

function onPageChange() {
  store.actions.fetchData()
}

function getSmartFolderDisplayName(item: any): string {
  // Handle section items
  if (item.type === 'section') {
    return item.name || ''
  }

  // For smart folder items, we use the value directly
  if (item.type === 'smart_folder') {
    // Check if it's in the current hierarchy level dictionary
    if (store.state.hierarchyLevels?.length) {
      const currentLevel = store.state.currentPath?.length || 0
      const hierarchyLevel = store.state.hierarchyLevels?.[currentLevel]
      const displayNameDict = hierarchyLevel?.display_names_by_value || {}

      // If we have a display name mapping, use it
      if (displayNameDict && item.value in displayNameDict) {
        return displayNameDict[item.value]
      }
    }

    // Otherwise, return the value directly
    return String(item.value || '')
  }

  // Default fallback for any other item type
  return item.name || item.value || ''
}

const sortStorageKey = computed(() => {
  let key = `${props.storageKey}-sort` || 'section-management-sort'
  if (myUser.value?.id) key = `${key}-user-${myUser.value.id}`
  return key
})

const persistedSort = useLocalStorage<{
  folders: { field: string; order: 'asc' | 'desc' }
  sections: { field: string; order: 'asc' | 'desc' }
}>(sortStorageKey.value, {
  folders: {
    field: 'default',
    order: 'asc',
  },
  sections: {
    field: 'default',
    order: 'asc',
  },
})

const getInitialSort = function (): { field: string; order: 'asc' | 'desc' } {
  if (!launchDarkly.value.enable_sorting_in_sections) return DEFAULT_SORTING
  if (store.state.viewMode === 'folders') return persistedSort.value.folders
  if (store.state.viewMode === 'sections') return persistedSort.value.sections

  return DEFAULT_SORTING
}

const currentSort = ref<{ field: string; order: 'asc' | 'desc' }>(getInitialSort())

watch(
  () => [store.state.viewMode, launchDarkly.value.enable_sorting_in_sections],
  ([newViewMode, sortingEnabled]) => {
    if (newViewMode && sortingEnabled) {
      if (newViewMode === 'folders') {
        currentSort.value = persistedSort.value.folders
      } else if (newViewMode === 'sections') {
        currentSort.value = persistedSort.value.sections
      }
    } else if (!sortingEnabled) {
      currentSort.value = DEFAULT_SORTING
    }
  },
  { immediate: true },
)

function onSortChanged(sort: { field: string; order: 'asc' | 'desc' }) {
  currentSort.value = sort

  if (launchDarkly.value.enable_sorting_in_sections) {
    if (store.state.viewMode === 'folders') {
      persistedSort.value.folders = sort
    } else if (store.state.viewMode === 'sections') {
      persistedSort.value.sections = sort
    }
  }
}

function calculateContentHeight() {
  if (!isSelector.value || !toolbarRef.value || !containerRef.value) {
    contentHeight.value = 'auto'
    return
  }

  nextTick(() => {
    const containerHeight = containerRef.value?.offsetHeight || 0
    const toolbarHeight = toolbarRef.value?.offsetHeight || 0
    const availableHeight = containerHeight - toolbarHeight
    contentHeight.value = `${Math.max(availableHeight, 200)}px`
  })
}

onMounted(async () => {
  // Configure draft inclusion based on mode
  if (isSelector.value) {
    // In selector mode, exclude drafts by default
    store.actions.setIncludeDraft(false)
  } else {
    // In management mode, include drafts by default
    store.actions.setIncludeDraft(true)
  }

  await store.actions.loadConfiguration()
  if (store.state.hierarchyLevels && store.state.hierarchyLevels.length > 0) {
    if (storedPath.value.length > 0) {
      storedPath.value.forEach((pathValue, index) => {
        store.actions.selectValue(pathValue, { skipFetchData: index < storedPath.value.length - 1 })
      })
    } else {
      store.actions.navigateToLevel(0)
    }
  }

  // Calculate height for selector mode
  if (isSelector.value) {
    calculateContentHeight()
  }
})

// Watch for changes that might affect height calculation
watch(
  () => [store.state.hierarchyLevels, store.state.viewMode, isSelector.value],
  () => {
    if (isSelector.value) {
      nextTick(() => {
        calculateContentHeight()
      })
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  store.actions.navigateToLevel(0)
})

function addSelectedSections(position?: 'top' | 'bottom') {
  if (!isSelector.value) return

  const selectedSections = getSelectedSections()
  if (selectedSections.length > 0) {
    emit('sections-bulk-add', selectedSections, position)
  }
}

function removeSelectedSections() {
  if (!isSelector.value) return

  const selectedSections = getSelectedSections()
  if (selectedSections.length > 0) {
    emit('sections-bulk-remove', selectedSections)
  }
}

defineExpose({
  showConfigurationModal: onSmartFoldersConfigure,
  addSelectedSections,
  removeSelectedSections,
  getSelectedSections,
  calculateContentHeight,
})
</script>
